import { FunctionComponent, useCallback } from 'react';
import {
    Box,
    Divider,
    FormControlLabel,
    MenuItem,
    Slider,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import {
    VoucherTemplateElement,
    VoucherTemplateElementType,
    VoucherTemplateAlignment,
    VoucherFontDTO,
    ELEMENT_TYPE_LABELS,
} from '@starjack/entities/voucherTemplate';
import {
    FormatAlignLeftIcon,
    FormatAlignCenterIcon,
    FormatAlignRightIcon,
} from './icons';

type PropertyPanelProps = {
    selectedElement: VoucherTemplateElement | null;
    selectedType: VoucherTemplateElementType | null;
    availableFonts: VoucherFontDTO[];
    onUpdate: (
        type: VoucherTemplateElementType,
        changes: Partial<VoucherTemplateElement>
    ) => void;
};

export const PropertyPanel: FunctionComponent<PropertyPanelProps> = ({
    selectedElement,
    selectedType,
    availableFonts,
    onUpdate,
}) => {
    const handleChange = useCallback(
        (changes: Partial<VoucherTemplateElement>) => {
            if (selectedType) {
                onUpdate(selectedType, changes);
            }
        },
        [selectedType, onUpdate]
    );

    if (!selectedElement || !selectedType) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant='body2' color='text.secondary'>
                    Zum Bearbeiten auf ein Element klicken.
                </Typography>
            </Box>
        );
    }

    return (
        <Stack spacing={2} sx={{ p: 2, overflow: 'auto', maxHeight: '100%' }}>
            {/* Header */}
            <Typography variant='subtitle1' fontWeight={600}>
                {ELEMENT_TYPE_LABELS[selectedType]}
            </Typography>

            <Divider />

            {/* Typography Section */}
            <Typography variant='caption' color='text.secondary'>
                TYPOGRAFIE
            </Typography>

            <TextField
                select
                label='Schriftart'
                value={selectedElement.font}
                onChange={(e) => handleChange({ font: e.target.value })}
                size='small'
                fullWidth
            >
                {availableFonts.map((f) => (
                    <MenuItem key={f.id} value={f.name}>
                        {f.name}
                    </MenuItem>
                ))}
                {availableFonts.length === 0 && (
                    <MenuItem value={selectedElement.font}>
                        {selectedElement.font}
                    </MenuItem>
                )}
            </TextField>

            <TextField
                label='Schriftgröße'
                type='number'
                value={selectedElement.fontSize}
                onChange={(e) =>
                    handleChange({
                        fontSize: Math.max(1, parseInt(e.target.value) || 12),
                    })
                }
                size='small'
                inputProps={{ min: 1, max: 200 }}
            />

            <Stack direction='row' spacing={1}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={selectedElement.bold}
                            onChange={(e) =>
                                handleChange({ bold: e.target.checked })
                            }
                            size='small'
                        />
                    }
                    label='Fett'
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={selectedElement.italic}
                            onChange={(e) =>
                                handleChange({ italic: e.target.checked })
                            }
                            size='small'
                        />
                    }
                    label='Kursiv'
                />
            </Stack>

            <Divider />

            {/* Color Section */}
            <Typography variant='caption' color='text.secondary'>
                FARBE
            </Typography>

            <Stack direction='row' spacing={1} alignItems='center'>
                <Typography variant='body2' sx={{ minWidth: 80 }}>
                    Textfarbe
                </Typography>
                <input
                    type='color'
                    value={rgbToHex(
                        selectedElement.colorR,
                        selectedElement.colorG,
                        selectedElement.colorB
                    )}
                    onChange={(e) => {
                        const rgb = hexToRgb(e.target.value);
                        if (rgb) {
                            handleChange({
                                colorR: rgb.r,
                                colorG: rgb.g,
                                colorB: rgb.b,
                            });
                        }
                    }}
                    style={{
                        width: 40,
                        height: 30,
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        cursor: 'pointer',
                    }}
                />
            </Stack>

            <FormControlLabel
                control={
                    <Switch
                        checked={(selectedElement.outlineWidth ?? 0) > 0}
                        onChange={(e) =>
                            handleChange({
                                // Clear the width instead of setting 0: the outline colour is
                                // dropped along with it, and a width without a colour is not a
                                // state the backend can map back to an outline.
                                outlineWidth: e.target.checked ? 1 : undefined,
                                ...(e.target.checked
                                    ? {}
                                    : {
                                          outlineColorR: undefined,
                                          outlineColorG: undefined,
                                          outlineColorB: undefined,
                                      }),
                            })
                        }
                        size='small'
                    />
                }
                label='Outline'
            />

            {(selectedElement.outlineWidth ?? 0) > 0 && (
                <Stack direction='row' spacing={1} alignItems='center'>
                    <TextField
                        label='Breite'
                        type='number'
                        value={selectedElement.outlineWidth ?? 1}
                        onChange={(e) =>
                            handleChange({
                                outlineWidth: Math.max(
                                    0,
                                    parseFloat(e.target.value) || 0
                                ),
                            })
                        }
                        size='small'
                        sx={{ width: 80 }}
                        inputProps={{ min: 0, max: 10, step: 0.5 }}
                    />
                    <input
                        type='color'
                        value={rgbToHex(
                            selectedElement.outlineColorR ?? 0,
                            selectedElement.outlineColorG ?? 0,
                            selectedElement.outlineColorB ?? 0
                        )}
                        onChange={(e) => {
                            const rgb = hexToRgb(e.target.value);
                            if (rgb) {
                                handleChange({
                                    outlineColorR: rgb.r,
                                    outlineColorG: rgb.g,
                                    outlineColorB: rgb.b,
                                });
                            }
                        }}
                        style={{
                            width: 40,
                            height: 30,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            cursor: 'pointer',
                        }}
                    />
                </Stack>
            )}

            <Divider />

            {/* Layout Section */}
            <Typography variant='caption' color='text.secondary'>
                LAYOUT
            </Typography>

            <Box>
                <Typography variant='body2' sx={{ mb: 0.5 }}>
                    Ausrichtung
                </Typography>
                <ToggleButtonGroup
                    value={selectedElement.alignment}
                    exclusive
                    onChange={(_e, val) => {
                        if (val !== null) {
                            handleChange({
                                alignment: val as VoucherTemplateAlignment,
                            });
                        }
                    }}
                    size='small'
                >
                    <ToggleButton value={0}>
                        <FormatAlignLeftIcon />
                    </ToggleButton>
                    <ToggleButton value={1}>
                        <FormatAlignCenterIcon />
                    </ToggleButton>
                    <ToggleButton value={2}>
                        <FormatAlignRightIcon />
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Box>
                <Typography variant='body2' sx={{ mb: 0.5 }}>
                    Rotation: {selectedElement.rotation}°
                </Typography>
                <Slider
                    value={selectedElement.rotation}
                    onChange={(_e, val) =>
                        handleChange({ rotation: val as number })
                    }
                    min={-180}
                    max={180}
                    step={1}
                    size='small'
                />
            </Box>

            <Divider />

            {/* Position Section */}
            <Typography variant='caption' color='text.secondary'>
                POSITION
            </Typography>

            <Stack direction='row' spacing={1}>
                <TextField
                    label='X'
                    type='number'
                    value={Math.round(selectedElement.x * 1000) / 1000}
                    onChange={(e) =>
                        handleChange({
                            x: Math.max(
                                0,
                                Math.min(1, parseFloat(e.target.value) || 0)
                            ),
                        })
                    }
                    size='small'
                    inputProps={{ min: 0, max: 1, step: 0.01 }}
                />
                <TextField
                    label='Y'
                    type='number'
                    value={Math.round(selectedElement.y * 1000) / 1000}
                    onChange={(e) =>
                        handleChange({
                            y: Math.max(
                                0,
                                Math.min(1, parseFloat(e.target.value) || 0)
                            ),
                        })
                    }
                    size='small'
                    inputProps={{ min: 0, max: 1, step: 0.01 }}
                />
            </Stack>

            <Stack direction='row' spacing={1}>
                <TextField
                    label='Breite'
                    type='number'
                    value={Math.round(selectedElement.w * 1000) / 1000}
                    onChange={(e) =>
                        handleChange({
                            w: Math.max(
                                0.01,
                                Math.min(1, parseFloat(e.target.value) || 0.1)
                            ),
                        })
                    }
                    size='small'
                    inputProps={{ min: 0.01, max: 1, step: 0.01 }}
                />
                <TextField
                    label='Höhe'
                    type='number'
                    value={Math.round(selectedElement.h * 1000) / 1000}
                    onChange={(e) =>
                        handleChange({
                            h: Math.max(
                                0.01,
                                Math.min(1, parseFloat(e.target.value) || 0.1)
                            ),
                        })
                    }
                    size='small'
                    inputProps={{ min: 0.01, max: 1, step: 0.01 }}
                />
            </Stack>
        </Stack>
    );
};

// Color conversion helpers
const rgbToHex = (r: number, g: number, b: number): string => {
    const toHex = (c: number) =>
        Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null;
};
